/**
 * The contract between a Shader node's host and the code running in its
 * sandboxed iframe.
 *
 * Three things depend on it and must agree exactly: the runtime
 * (`genie-runtime.ts`) implements it, the AI system prompt teaches it to the
 * model, and the node wires connected media into it. All three import from here
 * rather than restating names, so a renamed uniform fails the build instead of
 * silently rendering black.
 */

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/** Media a shader can consume, in the order their handles stack on the node. */
export const SHADER_INPUT_TYPES = ['image', 'video', 'audio', 'text', '3d'] as const
export type ShaderInputType = (typeof SHADER_INPUT_TYPES)[number]

export function isShaderInputType(value: unknown): value is ShaderInputType {
  return typeof value === 'string' && (SHADER_INPUT_TYPES as readonly string[]).includes(value)
}

/** React Flow target handle id on the Shader node, per input. */
export const SHADER_INPUT_HANDLE_IDS: Record<ShaderInputType, string> = {
  image: 'image-input',
  video: 'video-input',
  audio: 'audio-input',
  text: 'text-input',
  '3d': '3d-input',
}

/** Canvas `dataType` each input accepts from an upstream output handle. */
export const SHADER_INPUT_DATA_TYPES: Record<ShaderInputType, 'image' | 'video' | 'audio' | 'text' | '3d'> = {
  image: 'image',
  video: 'video',
  audio: 'audio',
  text: 'text',
  '3d': '3d',
}

export const SHADER_INPUT_LABELS: Record<ShaderInputType, string> = {
  image: 'Image',
  video: 'Video',
  audio: 'Audio',
  text: 'Text',
  '3d': '3D Model',
}

/**
 * Where each connected input comes from, resolved on the host from upstream
 * nodes. Every value is a URL (http, blob or data) except `text`, which is the
 * literal text.
 */
export interface ShaderInputSources {
  image?: string
  video?: string
  /** An audio file. */
  audio?: string
  text?: string
  /** A binary glTF (.glb). */
  '3d'?: string
}

// ---------------------------------------------------------------------------
// Host → iframe payloads (the `data` of a `genie:input` message)
// ---------------------------------------------------------------------------

/**
 * Audio analysis, computed on the host with a Web Audio `AnalyserNode`
 * (`fftSize` 2048) because the sandbox cannot reach the host's audio.
 *
 * The shapes are Web Audio's own, and the four library audio sketches depend
 * on them: `fft` is `getFloatFrequencyData` in decibels (about -140..0, read as
 * `(v + 140) / 140`), `waveform` is `getFloatTimeDomainData` in -1..1.
 */
export const AUDIO_FFT_SIZE = 2048
export const AUDIO_BIN_COUNT = AUDIO_FFT_SIZE / 2
/** Decibel floor used for silence and for disconnected audio. */
export const AUDIO_SILENCE_DB = -140

export interface ShaderAudioFrame {
  fft: Float32Array
  waveform: Float32Array
  /** RMS of the waveform, 0..1. */
  amplitude: number
}

/** Video travels as bytes: the opaque-origin sandbox cannot read the host's URLs. */
export interface ShaderVideoPayload {
  bytes: ArrayBuffer
  mimeType: string
}

export interface ShaderModelMesh {
  /** xyz triples, already in model space with node transforms applied. */
  positions: Float32Array
  normals: Float32Array | null
  uvs: Float32Array | null
  indices: Uint32Array | null
}

/** A parsed .glb, reduced to plain arrays so any runtime can build geometry from it. */
export interface ShaderModelData {
  meshes: ShaderModelMesh[]
  bounds: { min: [number, number, number]; max: [number, number, number] }
}

export interface ShaderInputPayloads {
  /** A data URL. */
  image: string
  video: ShaderVideoPayload
  audio: ShaderAudioFrame
  text: string
  '3d': ShaderModelData
}

// ---------------------------------------------------------------------------
// GLSL runtime — uniforms a fragment shader can declare
// ---------------------------------------------------------------------------

/**
 * Sampler uniforms, the texture unit each is bound to, and the flag and size
 * uniforms that travel with it. A shader declares only the ones it uses.
 *
 * - `u_hasX` is `1.0` while that input is connected and loaded, else `0.0`, so a
 *   shader can fall back gracefully instead of sampling black.
 * - `u_imageResolution` / `u_videoResolution` are the source's pixel size.
 */
export const GLSL_INPUT_UNIFORMS = {
  image: { sampler: 'u_image', unit: 0, has: 'u_hasImage', resolution: 'u_imageResolution' },
  video: { sampler: 'u_video', unit: 1, has: 'u_hasVideo', resolution: 'u_videoResolution' },
  audio: { sampler: 'u_audio', unit: 2, has: 'u_hasAudio', resolution: null },
  text: { sampler: 'u_text', unit: 3, has: 'u_hasText', resolution: null },
  '3d': { sampler: 'u_model', unit: 4, has: 'u_hasModel', resolution: null },
} as const satisfies Record<ShaderInputType, { sampler: string; unit: number; has: string; resolution: string | null }>

/** Built-in uniforms available to every GLSL shader. */
export const GLSL_BUILTIN_UNIFORMS = {
  time: 'u_time',
  resolution: 'u_resolution',
  mouse: 'u_mouse',
  /** RMS loudness of the audio input, 0..1. */
  amplitude: 'u_amplitude',
} as const

/**
 * `u_audio` is `AUDIO_BIN_COUNT` wide and 2 rows tall.
 * - Row 0 (sample at `y = 0.25`): FFT magnitude, 0..1, low frequencies on the left.
 * - Row 1 (sample at `y = 0.75`): waveform, `0.5` is silence.
 */
export const GLSL_AUDIO_TEXTURE = { width: AUDIO_BIN_COUNT, height: 2, fftRow: 0.25, waveformRow: 0.75 } as const

/**
 * `u_model` is the 3D input rendered as a slow turntable, filling the canvas:
 * `rgb` is the view-space normal packed to 0..1 (`n * 0.5 + 0.5`), `a` is depth
 * (1 nearest, falling toward 0 at the back) and exactly 0 where there is no mesh.
 */
export const GLSL_MODEL_TEXTURE = { turntableSecondsPerTurn: 12 } as const

/**
 * `u_text` is the connected text at the canvas's own size: white bold
 * sans-serif glyphs (rgb 1, alpha is coverage) on transparent black, centred,
 * word-wrapped and sized as large as fits inside `fill` of the width and
 * height. Sample it at `v_texCoord` and use `.a` as a mask.
 */
export const GLSL_TEXT_TEXTURE = { fill: 0.9, color: '#ffffff', maxLength: 2000 } as const

/** Every uniform name the runtime binds itself; the rest map to `// @param`s. */
export const GLSL_RESERVED_UNIFORMS: readonly string[] = [
  ...Object.values(GLSL_BUILTIN_UNIFORMS),
  ...Object.values(GLSL_INPUT_UNIFORMS).flatMap((u) => [u.sampler, u.has, ...(u.resolution ? [u.resolution] : [])]),
]

// ---------------------------------------------------------------------------
// JavaScript runtimes (p5, three.js, raw) — the `window.genie` object
// ---------------------------------------------------------------------------

/**
 * What a p5 / three.js / raw sketch sees on `window.genie`. Documented here as
 * text because it lives inside the iframe, not in this bundle; the runtime must
 * provide exactly this and the system prompt quotes it verbatim.
 */
export const GENIE_JS_API = `
genie.inputImage   string | null   data URL of the connected image
genie.inputVideo   HTMLVideoElement | null   playing, muted, looping; pauses with the node. Usable as a p5 image, a THREE.VideoTexture source, or drawImage source.
genie.audio        { fft: Float32Array(${AUDIO_BIN_COUNT}), waveform: Float32Array(${AUDIO_BIN_COUNT}), amplitude: number }
                   fft is decibels (${AUDIO_SILENCE_DB}..0) — normalise with (v + 140) / 140. waveform is -1..1. amplitude is RMS 0..1.
                   Silent (fft all ${AUDIO_SILENCE_DB}, waveform all 0) when no audio is connected.
genie.inputText    string | null   the connected text
genie.inputModel   { meshes: [{ positions, normals, uvs, indices }], bounds: { min, max } } | null
                   typed arrays (Float32Array / Uint32Array); positions are xyz triples in model space.
genie.modelToThree(THREE)   THREE.Group | null   the model as meshes, centred at the origin and scaled to fit a unit sphere
genie.params       { [name]: value }   current values of the // @param controls
genie.width, genie.height   canvas size in pixels
genie.canvas       the page's <canvas> (null in p5 mode — createCanvas makes its own)
genie.onInput(handle, callback)   handle is 'image' | 'video' | 'audio' | 'text' | '3d'; called whenever that input changes
`.trim()
